export type NutritionRow = { name_sv: string; name_en: string; value: number | null; unit: string };

// A declaration has one numeric value per row, so energy uses separate kJ and kcal rows.
const STANDARD_NUTRITION_ROWS: ReadonlyArray<Omit<NutritionRow, "value">> = [
  { name_sv: "Energi (kJ)", name_en: "Energy (kJ)", unit: "kJ" },
  { name_sv: "Energi (kcal)", name_en: "Energy (kcal)", unit: "kcal" },
  { name_sv: "Fett", name_en: "Fat", unit: "g" },
  { name_sv: "varav mättat fett", name_en: "of which saturates", unit: "g" },
  { name_sv: "Kolhydrat", name_en: "Carbohydrate", unit: "g" },
  { name_sv: "varav sockerarter", name_en: "of which sugars", unit: "g" },
  { name_sv: "Fiber", name_en: "Fibre", unit: "g" },
  { name_sv: "Protein", name_en: "Protein", unit: "g" },
  { name_sv: "Salt", name_en: "Salt", unit: "g" },
];

const normalized = (value: string | undefined) => (value || "").trim().toLocaleLowerCase("sv-SE").replace(/\s+/g, " ");

const matchesStandard = (row: NutritionRow, standard: Omit<NutritionRow, "value">) => {
  const nameSv = normalized(row.name_sv);
  const nameEn = normalized(row.name_en);
  if (standard.unit === "kJ" || standard.unit === "kcal") {
    return normalized(row.unit) === normalized(standard.unit) &&
      (nameSv === normalized(standard.name_sv) || nameEn === normalized(standard.name_en) ||
        nameSv === "energi" || nameEn === "energy");
  }
  return nameSv === normalized(standard.name_sv) || nameEn === normalized(standard.name_en);
};

const isStandardNutritionRow = (row: NutritionRow) => STANDARD_NUTRITION_ROWS.some((standard) => matchesStandard(row, standard));

export const hasNutritionValue = (row: NutritionRow): row is NutritionRow & { value: number } =>
  typeof row.value === "number" && Number.isFinite(row.value) && row.value >= 0;

// Keep saved rows and their order; show only the missing standard rows as blank inputs.
export const withStandardNutritionRows = (savedRows: NutritionRow[]): NutritionRow[] => [
  ...savedRows,
  ...STANDARD_NUTRITION_ROWS.filter((standard) => !savedRows.some((row) => matchesStandard(row, standard)))
    .map((standard) => ({ ...standard, value: null })),
];

// Blank standard inputs are editor placeholders, not nutrition values to store.
export const nutritionRowsForSave = (rows: NutritionRow[]): NutritionRow[] =>
  rows.filter((row) => !isStandardNutritionRow(row) || hasNutritionValue(row))
    .map((row) => ({
      name_sv: row.name_sv.trim(),
      name_en: row.name_en.trim(),
      value: row.value,
      unit: row.unit.trim(),
    }));
